import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { User, WorkflowCustomField } from '../../../types';
import type {
  ChangeCatalogItem,
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeRequest,
  ChangeSubtask,
  ChangeSubtaskAttachment,
  ChangeWorkGroup,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { CHANGE_FIELD_LIMITS } from '../changeValidation';
import {
  ASSIGNMENT_PESSOA,
  ASSIGNMENT_SETOR,
  assignmentLabel,
  canAnswerChecklist as canAnswerByRule,
  canAssumeSubtask as canAssumeByRule,
  subtaskResponsibleLabel,
  canTalkOnSubtask as canTalkByRule,
  canAttachOnSubtask as canAttachByRule,
  peopleFromAreas,
  type SubtaskAssignmentKind,
} from '../changeSubtaskAssignment';
import {
  buildExtraFieldsPayloadFromNatives,
  hydrateExtraFields,
  validateExtraFieldsFromNatives,
  weekdayConstraintHint,
} from '../changeFormSchema';
import { dateOnlyToPythonWeekday } from '../goLiveWindow';
import ChangeSubtaskChecklist from './ChangeSubtaskChecklist';
import ChangeSubtaskConfigurableForm, {
  subtaskNativeMap,
  type SubtaskCreateFormValues,
} from './ChangeSubtaskConfigurableForm';
import {
  FILE_ONLY_REPLY,
  SUBTASK_REPLY_LABEL,
  formatReplyWhen,
  isBlankRichText,
  prepareSubtaskReplyHtml,
  splitSubtaskAttachments,
} from '../changeSubtaskThread';
import {
  FORM_EDITABLE_STATUSES,
  FROZEN_STATUSES,
  SUBTASK_OPERATE_STATUSES,
  SUBTASK_STATUS_LABELS,
  subtaskListStatus,
} from '../changeLabels';
import { changeAllowsSubtaskPackage } from '../subtaskPackageCreateStatuses';
import RichTextCommentEditor from '../../../components/RichTextCommentEditor';
import { downloadFile } from '../../../utils/fileDownload';
import { sanitizeRichTextHtml } from '../../../utils/richTextSanitizer';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../../../utils/uploadValidation';
import { formatGovernanceDate, toLocalDateInput } from '../goLiveWindow';
import { policyFlagsForType } from '../changeTypePolicy';
import { emptyIncidentDraft, incidentAllowsAttachments, incidentCustomFieldDefaultsForWorkflow, incidentCustomFieldsPayload, type IncidentSendDraft } from '../incidentQuickSend';
import {
  incidentFileFieldIds,
  serializeIncidentCustomFields,
  uploadIncidentCustomFieldFiles,
  validateIncidentTicketFields,
  type IncidentCustomFieldFiles,
  type IncidentCustomFieldValues,
} from '../incidentTicketFields';
import IncidentTicketFieldsForm from './IncidentTicketFieldsForm';
import IncidentSendPackageForm from './IncidentSendPackageForm';
import ChangeAddSubtasksPackageModal from './ChangeAddSubtasksPackageModal';
import { occupyingSubtaskGroupIds } from '../changeCreateReview';
import { canShowRedispatchPackage, isChangeManager } from '../changePermissions';
import {
  changeHasInfraImplementationGate,
  IMPLEMENTATION_GATE_HELP,
  isInfraIncidentArea,
} from '../changeInfraIncidentAreas';
import {
  INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS,
  INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS,
  isInternalSubtask,
} from '../changeInternalSubtaskTableColumns';
import { layoutColumnsFromApi } from '../changeInternalSubtaskListLayout';
import {
  canEditRequesterChecklistFields,
  shouldUseUnifiedChecklistEditor,
  splitChecklistByRespondent,
} from '../changeSubtaskChecklistAccess';
import ChangeInternalSubtaskListTable from './ChangeInternalSubtaskListTable';
import type { InternalSubtaskListLayoutColumnView } from '../changeInternalSubtaskListLayout';

interface Props {
  change: ChangeRequest;
  onUpdated: () => Promise<void> | void;
}

interface ReplyItem {
  id: number;
  user_id: number;
  user_name?: string | null;
  content: string;
  created_at: string;
}

function subtaskStatusBadge(subtask: Pick<ChangeSubtask, 'status' | 'invalidated_at'>) {
  const status = subtaskListStatus(subtask);
  if (status === 'cancelada') return <Badge bg="secondary">Cancelada</Badge>;
  const label = SUBTASK_STATUS_LABELS[status] || status;
  if (status === 'concluida') return <Badge bg="success">{label}</Badge>;
  if (status === 'em_atendimento') return <Badge bg="primary">{label}</Badge>;
  return <Badge bg="secondary">{label}</Badge>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Col md={6} className="mb-2">
      <div className="small text-muted">{label}</div>
      <div>{children || '—'}</div>
    </Col>
  );
}

function formatStoredExtra(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { start?: string; end?: string };
    if (parsed && typeof parsed === 'object' && parsed.start && parsed.end) {
      return `${parsed.start} – ${parsed.end}`;
    }
  } catch {
    /* valor simples */
  }
  return raw;
}

function checklistItems(subtask: ChangeSubtask): ChangeFormField[] {
  return (subtask.checklist?.items || []).map((item) => ({
    ...item,
    kind: item.kind || 'extra',
  }));
}

function isListableSubtask(subtask: ChangeSubtask): boolean {
  if (subtask.origin === 'checklist' && checklistItems(subtask).length === 0) return false;
  return true;
}

function sortListedSubtasks(items: ChangeSubtask[]): ChangeSubtask[] {
  const active: ChangeSubtask[] = [];
  const canceled: ChangeSubtask[] = [];
  items.forEach((item) => {
    if (item.invalidated_at) canceled.push(item);
    else active.push(item);
  });
  return active.concat(canceled);
}

function showsSubtaskThread(subtask: ChangeSubtask): boolean {
  if (subtask.origin === 'incidente') return false;
  if (subtask.origin !== 'checklist') return true;
  return Boolean(subtask.allow_thread);
}

function checklistHasAnswers(subtask: ChangeSubtask): boolean {
  const answers = subtask.checklist?.answers || {};
  return Object.values(answers).some((value) => String(value || '').trim() !== '');
}

function incidentHeaderHint(subtask: ChangeSubtask): string | null {
  if (subtask.origin !== 'incidente') return null;
  if (subtask.incident?.outcome === 'sucesso' || subtask.incident_outcome === 'sucesso') {
    return 'Encerrado com sucesso';
  }
  if (subtask.incident?.outcome === 'insucesso' || subtask.incident_outcome === 'insucesso') {
    return 'Encerrado sem sucesso';
  }
  if (subtask.incident?.taken_by_name) return 'Em atendimento no incidente';
  return 'Aguardando o grupo no Incidente';
}

function checklistHeaderHint(
  subtask: ChangeSubtask,
  canFill: boolean,
  canAssume: boolean,
): string | null {
  const count = checklistItems(subtask).length;
  if (!count) return null;
  if (canFill) return count === 1 ? '1 pergunta para responder' : `${count} perguntas para responder`;
  if (canAssume) return 'Assuma para responder';
  return checklistHasAnswers(subtask) ? 'Respondido pelo grupo' : 'Aguardando o grupo';
}

function subtaskMemberLabel(subtask: ChangeSubtask): string {
  return assignmentLabel(subtask.assignment_kind, subtask.assignee_name);
}

function subtaskTypeLabel(subtask: ChangeSubtask): string {
  return (subtask.task_type_name || '').trim() || '—';
}

function extraEntries(
  extraFields: Record<string, string | null> | undefined,
  schema: ChangeFormField[],
): Array<{ id: string; label: string; value: string }> {
  if (!extraFields) return [];
  const labels = new Map(
    schema.filter((field) => field.kind === 'extra').map((field) => [String(field.id), field.label]),
  );
  return Object.entries(extraFields)
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([id, value]) => ({
      id,
      label: labels.get(id) || `Campo ${id}`,
      value: formatStoredExtra(String(value)),
    }));
}

function AttachmentLine({
  file,
  canRemove,
  onRemove,
  onDownload,
}: {
  file: ChangeSubtaskAttachment;
  canRemove: boolean;
  onRemove: () => void;
  onDownload: () => void;
}) {
  return (
    <li className="d-flex align-items-center gap-2">
      {file.file_path ? (
        <Button size="sm" variant="outline-secondary" onClick={onDownload}>
          {file.filename}
        </Button>
      ) : (
        <span>{file.filename}</span>
      )}
      {canRemove ? (
        <Button size="sm" variant="outline-danger" onClick={onRemove}>
          Remover
        </Button>
      ) : null}
    </li>
  );
}

const ChangeSubtasksTab: React.FC<Props> = ({ change, onUpdated }) => {
  const { user } = useAuth();
  const { showConfirm, showAlert } = useModal();
  const [packageCreateStatuses, setPackageCreateStatuses] = useState<string[] | null>(null);
  const canCreate =
    changeAllowsSubtaskPackage(change.status, packageCreateStatuses) &&
    change.can_create_subtask !== false;
  const subtaskPolicy =
    change.change_type_policy ?? policyFlagsForType(change.change_type, []);
  const allowInternalSubtasks = subtaskPolicy.enable_internal_subtasks;
  const allowExternalSubtasks = subtaskPolicy.enable_external_subtasks;
  const canQuickSendIncident =
    change.can_create_subtask !== false &&
    (change.status === 'avaliar' || change.status === 'transporte');
  const canOperate = SUBTASK_OPERATE_STATUSES.has(change.status);
  const [areas, setAreas] = useState<ChangeWorkGroup[]>([]);
  const [title, setTitle] = useState('');
  const [assignmentKind, setAssignmentKind] = useState<SubtaskAssignmentKind>(ASSIGNMENT_SETOR);
  const [groupId, setGroupId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [comments, setComments] = useState('');
  const [urgency, setUrgency] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [createCustomFields, setCreateCustomFields] = useState<WorkflowCustomField[]>([]);
  const [createCustomValues, setCreateCustomValues] = useState<IncidentCustomFieldValues>({});
  const [createCustomFiles, setCreateCustomFiles] = useState<IncidentCustomFieldFiles>({});
  const [incidentTargets, setIncidentTargets] = useState<{
    groups: Array<{ id: number; name: string }>;
    workflows: Array<{ id: number; name: string; group_ids: number[] }>;
  }>({ groups: [], workflows: [] });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<number, ReplyItem[]>>({});
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [pendingFiles, setPendingFiles] = useState<Record<number, File[]>>({});
  const [fileInputKey, setFileInputKey] = useState<Record<number, number>>({});
  const [schema, setSchema] = useState<ChangeFormField[]>([]);
  const [checklistDrafts, setChecklistDrafts] = useState<Record<number, ChangeExtraFields>>({});
  const [savingChecklistId, setSavingChecklistId] = useState<number | null>(null);
  const [extras, setExtras] = useState<ChangeExtraFields>({});
  const [allowCreateAttachments, setAllowCreateAttachments] = useState(false);
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [quickDrafts, setQuickDrafts] = useState<Record<number, IncidentSendDraft>>({});
  const [quickSending, setQuickSending] = useState<number | null>(null);
  const [sendIncidentGroupId, setSendIncidentGroupId] = useState<number | null>(null);
  const [taskTypes, setTaskTypes] = useState<ChangeCatalogItem[]>([]);
  const [taskTypeId, setTaskTypeId] = useState('');
  const [openSubtaskId, setOpenSubtaskId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [internalListColumns, setInternalListColumns] = useState<InternalSubtaskListLayoutColumnView[]>(
    [],
  );
  const [packageModalPrefill, setPackageModalPrefill] = useState(false);
  const [resettingPackage, setResettingPackage] = useState(false);
  const [createGatesImplementar, setCreateGatesImplementar] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      changesApi.listWorkGroups({ only_active: true }),
      changesApi.listIncidentTargets().catch(() => ({ groups: [], workflows: [] })),
      changesApi.listCatalog('task-types', { only_active: true }).catch(() => ({ items: [] })),
    ]).then(([res, targets, catalog]) => {
      if (cancelled) return;
      const items = res.items || [];
      setAreas(items);
      setIncidentTargets(targets);
      setTaskTypes(catalog.items || []);
      setQuickDrafts((prev) => {
        const next = { ...prev };
        for (const group of items) {
          if (group.open_in_incidents && group.incidents_group_id) {
            next[group.group_id] = emptyIncidentDraft(
              group,
              change.status === 'transporte' ? { blankWorkflow: true } : undefined,
            );
          }
        }
        return next;
      });
    }).catch(() => {
      if (!cancelled) setAreas([]);
    });
    return () => {
      cancelled = true;
    };
  }, [change.status, change.id, change.subtasks]);

  useEffect(() => {
    let cancelled = false;
    void changesApi
      .getSubtaskFormSchema()
      .then((data) => {
        if (cancelled) return;
        const items = data.items || [];
        setSchema(items);
        setExtras(hydrateExtraFields(items));
        setAllowCreateAttachments(Boolean(data.allow_create_attachments));
        setPackageCreateStatuses(data.package_create_statuses || null);
      })
      .catch(() => {
        if (!cancelled) setSchema([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setChecklistDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const subtask of change.subtasks || []) {
        const items = checklistItems(subtask);
        if (!items.length || next[subtask.id]) continue;
        next[subtask.id] = hydrateExtraFields(items, subtask.checklist?.answers);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [change.subtasks]);

  useEffect(() => {
    const withTicket = (change.subtasks || []).filter(
      (item) => item.ticket_id && showsSubtaskThread(item),
    );
    if (!withTicket.length) return;
    let cancelled = false;
    void changesApi.listChangeSubtaskReplies(change.id).then((response) => {
      if (cancelled) return;
      const next: Record<number, ReplyItem[]> = {};
      withTicket.forEach((item) => {
        next[item.id] = [];
      });
      (response.items || []).forEach((item) => {
        const list = next[item.subtask_id] || [];
        list.push({
          id: item.id,
          user_id: item.user_id,
          user_name: item.user_name,
          content: item.content,
          created_at: item.created_at,
        });
        next[item.subtask_id] = list;
      });
      setReplies((prev) => ({ ...prev, ...next }));
    }).catch(() => {
      if (!cancelled) {
        setError('Não foi possível carregar uma ou mais conversas da subtarefa.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [change.id, change.subtasks]);

  const windowStart = toLocalDateInput(change.go_live_start_at);
  const windowEnd = toLocalDateInput(change.go_live_end_at);
  const windowHelp =
    windowStart && windowEnd
      ? windowStart === windowEnd
        ? `Só ${formatGovernanceDate(windowStart)}: o Go Live começa e termina no mesmo dia.`
        : `Entre ${formatGovernanceDate(windowStart)} e ${formatGovernanceDate(windowEnd)} (início e fim do Go Live).`
      : 'A data precisa estar entre o início e o fim do Go Live.';
  const createForm: SubtaskCreateFormValues = {
    title,
    assignmentKind,
    groupId,
    assigneeId,
    dueDate,
    comments,
    taskTypeId,
  };
  const selectedArea = areas.find((area) => String(area.group_id) === groupId);
  const opensIncident = Boolean(selectedArea?.open_in_incidents);
  const managerUser = isChangeManager(user);
  const canOfferImplementationGate =
    managerUser &&
    opensIncident &&
    Boolean(selectedArea && isInfraIncidentArea(selectedArea)) &&
    !changeHasInfraImplementationGate(change);
  const showCreateAttachments = opensIncident
    ? Boolean(selectedArea && incidentAllowsAttachments(selectedArea))
    : allowCreateAttachments;
  const incidentWorkflows = incidentTargets.workflows;
  const useConfigurableForm = schema.some((field) => field.kind === 'native');
  const hasIncidentAreas = areas.some(
    (area) => area.open_in_incidents && area.incidents_group_id,
  );
  const areasForManualCreate = useMemo(() => {
    if (allowInternalSubtasks) return areas;
    return areas.filter((area) => area.open_in_incidents && area.incidents_group_id);
  }, [areas, allowInternalSubtasks]);
  const showManualCreate = canCreate && areas.length > 0;
  const quickGroups = useMemo(() => {
    const sent = new Set(
      (change.subtasks || [])
        .filter((item) => item.origin === 'incidente')
        .map((item) => item.group_id),
    );
    const eligible = areas.filter(
      (area) => area.open_in_incidents && area.incidents_group_id && !sent.has(area.group_id),
    );
    return eligible;
  }, [areas, change.subtasks, change.status]);
  const quickIncidentGroups = allowExternalSubtasks ? quickGroups : [];

  const applyGroupSelection = (nextGroupId: string) => {
    setGroupId(nextGroupId);
    const area = areas.find((item) => String(item.group_id) === nextGroupId);
    const nextWorkflow = area?.incidents_workflow_id ? String(area.incidents_workflow_id) : '';
    setWorkflowId(nextWorkflow);
    setCreateCustomValues(
      area ? incidentCustomFieldDefaultsForWorkflow(area, Number(nextWorkflow) || '') : {},
    );
    setCreateCustomFiles({});
  };

  const applyIncidentWorkflow = (nextWorkflowId: string) => {
    setWorkflowId(nextWorkflowId);
    setCreateCustomValues(
      selectedArea
        ? incidentCustomFieldDefaultsForWorkflow(selectedArea, Number(nextWorkflowId) || '')
        : {},
    );
    setCreateCustomFiles({});
  };

  const resetCreateForm = () => {
    setTitle('');
    setAssignmentKind(ASSIGNMENT_SETOR);
    setGroupId('');
    setAssigneeId('');
    setDueDate('');
    setComments('');
    setTaskTypeId('');
    setUrgency('');
    setWorkflowId('');
    setCreateCustomFields([]);
    setCreateCustomValues({});
    setCreateCustomFiles({});
    setExtras(hydrateExtraFields(schema));
    setCreateGatesImplementar(false);
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Informe o título da subtarefa.');
      return;
    }
    if (assignmentKind === ASSIGNMENT_SETOR && !groupId) {
      setError('Selecione o grupo que vai receber a subtarefa.');
      return;
    }
    if (assignmentKind === ASSIGNMENT_PESSOA && !assigneeId) {
      setError('Selecione a pessoa que vai receber a subtarefa.');
      return;
    }
    const typeField = schema.find((field) => field.native_key === 'task_type_id');
    const typeRequired = typeField ? Boolean(typeField.is_visible && typeField.is_required) : false;
    if (typeRequired && !taskTypeId) {
      setError('Selecione o tipo da tarefa.');
      return;
    }
    const dueField = schema.find((field) => field.native_key === 'due_date');
    const dueRequired = dueField ? Boolean(dueField.is_visible && dueField.is_required) : false;
    if (dueRequired && !dueDate) {
      setError('Informe a data da subtarefa.');
      return;
    }
    if (dueDate && (dueDate < windowStart || dueDate > windowEnd)) {
      setError('A data da subtarefa precisa estar dentro da janela de Go Live.');
      return;
    }
    if (
      dueDate &&
      dueField?.weekday_constraint != null &&
      dateOnlyToPythonWeekday(dueDate) !== dueField.weekday_constraint
    ) {
      setError(weekdayConstraintHint(dueField.weekday_constraint) || 'Data inválida.');
      return;
    }
    const commentsField = schema.find((field) => field.native_key === 'comments');
    if (opensIncident && !urgency) {
      setError('Informe a urgência do chamado.');
      return;
    }
    if (opensIncident && !workflowId && !selectedArea?.incidents_workflow_id) {
      setError('Escolha o fluxo de incidentes desta fila.');
      return;
    }
    if (opensIncident && !comments.trim()) {
      setError('Informe a descrição do chamado para o módulo de Incidentes.');
      return;
    }
    if (opensIncident && selectedArea && !incidentAllowsAttachments(selectedArea) && createFiles.length) {
      setError('Este chamado não aceita anexo.');
      return;
    }
    if (opensIncident) {
      const customError = validateIncidentTicketFields(
        createCustomFields,
        createCustomValues,
        createCustomFiles,
      );
      if (customError) {
        setError(customError);
        return;
      }
    }
    if (commentsField?.is_required && commentsField.is_visible && !comments.trim()) {
      setError('Informe o comentário inicial.');
      return;
    }
    const extraError = validateExtraFieldsFromNatives(schema, subtaskNativeMap(createForm), extras);
    if (extraError) {
      setError(extraError);
      return;
    }
    setError(null);
    setInfo(null);
    try {
      const areaName = areas.find((area) => String(area.group_id) === groupId)?.name || 'o grupo';
      const personName =
        peopleFromAreas(areas).find((member) => String(member.user_id) === assigneeId)?.name ||
        'a pessoa';
      const extraPayload = buildExtraFieldsPayloadFromNatives(
        schema,
        subtaskNativeMap(createForm),
        extras,
      );
      const body: Record<string, unknown> = {
        title: title.trim(),
        group_id: assignmentKind === ASSIGNMENT_SETOR ? Number(groupId) : null,
        due_date: dueDate || null,
        comments: comments.trim() || null,
        assignment_kind: assignmentKind,
        assignee_id: assignmentKind === ASSIGNMENT_PESSOA ? Number(assigneeId) : null,
      };
      if (taskTypeId) {
        body.task_type_id = Number(taskTypeId);
      }
      if (opensIncident) {
        body.urgency = urgency;
        body.workflow_id = Number(workflowId || selectedArea?.incidents_workflow_id);
        body.has_attachment = createFiles.length > 0;
        body.incident_custom_fields = serializeIncidentCustomFields(
          createCustomFields,
          createCustomValues,
        );
        body.incident_file_field_ids = incidentFileFieldIds(
          createCustomFields,
          createCustomFiles,
          createCustomValues,
        );
        if (createGatesImplementar && canOfferImplementationGate) {
          body.gates_implementar = true;
        }
      }
      if (Object.keys(extraPayload).length) {
        body.extra_fields = extraPayload;
      }
      const created = await changesApi.createSubtask(change.id, body);
      if ((allowCreateAttachments || opensIncident) && createFiles.length) {
        for (const file of createFiles) {
          const invalid = validateAttachmentFile(file);
          if (invalid) {
            setError(invalid);
            await onUpdated();
            return;
          }
          const formData = new FormData();
          formData.append('file', file);
          await changesApi.uploadSubtaskAttachment(change.id, created.id, formData);
        }
      }
      if (opensIncident && created.ticket_id) {
        await uploadIncidentCustomFieldFiles(created.ticket_id, createCustomFiles);
      }
      resetCreateForm();
      setCreateFiles([]);
      setShowCreateModal(false);
      setInfo(
        createGatesImplementar && canOfferImplementationGate
          ? `Subtarefa de gate de Implementar registrada para ${areaName}. O chamado será disparado após GTEC/GSERV-I aprovarem o Transporte.`
          : assignmentKind === ASSIGNMENT_PESSOA
            ? `Subtarefa enviada para ${personName} (${areaName}). Entrou no histórico desta aba.`
            : `Subtarefa enviada para ${areaName}. Entrou no histórico desta aba.`,
      );
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao criar a subtarefa.'));
    }
  };

  const handleQuickSend = async (group: ChangeWorkGroup) => {
    const draft =
      quickDrafts[group.group_id] ||
      emptyIncidentDraft(group, change.status === 'transporte' ? { blankWorkflow: true } : undefined);
    if (!draft.workflow_id) {
      setError(`Escolha o fluxo de incidentes de ${group.name}.`);
      return;
    }
    if (!draft.incidents_group_id) {
      setError(`Escolha o grupo para encaminhar o chamado de ${group.name}.`);
      return;
    }
    if (!draft.title.trim()) {
      setError(`Informe o título do chamado de ${group.name}.`);
      return;
    }
    if (!draft.comments.trim()) {
      setError(`Informe a descrição do chamado de ${group.name}.`);
      return;
    }
    if (!draft.urgency) {
      setError(`Informe a urgência do chamado de ${group.name}.`);
      return;
    }
    if (!incidentAllowsAttachments(group) && draft.files.length) {
      setError(`O chamado de ${group.name} não aceita anexo.`);
      return;
    }
    const customError = validateIncidentTicketFields(
      draft.customFields,
      draft.customFieldValues,
      draft.customFieldFiles,
    );
    if (customError) {
      setError(`${group.name}: ${customError}`);
      return;
    }
    const invalid = draft.files.map(validateAttachmentFile).find(Boolean);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setInfo(null);
    setQuickSending(group.group_id);
    try {
      const created = await changesApi.createSubtask(change.id, {
        title: draft.title.trim(),
        group_id: group.group_id,
        comments: draft.comments.trim(),
        assignment_kind: ASSIGNMENT_SETOR,
        urgency: draft.urgency,
        workflow_id: Number(draft.workflow_id),
        incidents_group_id: draft.incidents_group_id,
        has_attachment: draft.files.length > 0,
        due_date: windowStart || null,
        ...incidentCustomFieldsPayload(draft),
      });
      for (const file of draft.files) {
        const formData = new FormData();
        formData.append('file', file);
        await changesApi.uploadSubtaskAttachment(change.id, created.id, formData);
      }
      if (created.ticket_id) {
        await uploadIncidentCustomFieldFiles(created.ticket_id, draft.customFieldFiles);
      }
      setQuickDrafts((prev) => ({ ...prev, [group.group_id]: { ...draft, files: [] } }));
      setSendIncidentGroupId(null);
      setInfo(`Chamado enviado para ${group.name}.`);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o chamado.'));
    } finally {
      setQuickSending(null);
    }
  };

  const patchQuickDraft = (group: ChangeWorkGroup, patch: Partial<IncidentSendDraft>) => {
    setQuickDrafts((prev) => ({
      ...prev,
      [group.group_id]: { ...(prev[group.group_id] || emptyIncidentDraft(group)), ...patch },
    }));
  };

  const persistReply = async (subtask: ChangeSubtask) => {
    const content = prepareSubtaskReplyHtml(replyDrafts[subtask.id] || '');
    const files = pendingFiles[subtask.id] || [];
    if (isBlankRichText(content) && !files.length) return false;
    const result = await changesApi.addSubtaskReply(
      change.id,
      subtask.id,
      isBlankRichText(content) ? FILE_ONLY_REPLY : content,
    );
    const items = result.items || [];
    const lastReply = items[items.length - 1];
    setReplies((prev) => ({ ...prev, [subtask.id]: items }));
    setReplyDrafts((prev) => ({ ...prev, [subtask.id]: '' }));
    if (files.length) {
      const uploads = files.map((file) => {
        const form = new FormData();
        form.append('file', file);
        return changesApi.uploadSubtaskAttachment(change.id, subtask.id, form, lastReply?.id);
      });
      await Promise.all(uploads);
      setPendingFiles((prev) => ({ ...prev, [subtask.id]: [] }));
      setFileInputKey((prev) => ({ ...prev, [subtask.id]: (prev[subtask.id] || 0) + 1 }));
    }
    return true;
  };

  const persistChecklist = async (subtask: ChangeSubtask, requireRequired: boolean) => {
    const items = checklistItems(subtask);
    if (!items.length) return true;
    const extrasForSubtask =
      checklistDrafts[subtask.id] || hydrateExtraFields(items, subtask.checklist?.answers);
    if (requireRequired) {
      const extraError = validateExtraFieldsFromNatives(items, {}, extrasForSubtask);
      if (extraError) {
        setError(extraError);
        return false;
      }
    }
    await changesApi.saveSubtaskChecklist(change.id, subtask.id, {
      answers: buildExtraFieldsPayloadFromNatives(items, {}, extrasForSubtask),
    });
    return true;
  };

  const handleSaveChecklist = async (subtask: ChangeSubtask) => {
    setError(null);
    setSavingChecklistId(subtask.id);
    try {
      const saved = await persistChecklist(subtask, false);
      if (!saved) return;
      showAlert({
        title: 'Respostas salvas',
        message: 'As respostas do checklist foram salvas.',
        variant: 'success',
      });
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao salvar o checklist.'));
    } finally {
      setSavingChecklistId(null);
    }
  };

  const handleChecklistChange = (subtaskId: number, fieldId: number, value: ChangeExtraValue) => {
    setChecklistDrafts((prev) => ({
      ...prev,
      [subtaskId]: { ...(prev[subtaskId] || {}), [String(fieldId)]: value },
    }));
  };

  const handleConclude = async (subtask: ChangeSubtask) => {
    const items = checklistItems(subtask);
    if (items.length) {
      const extrasForSubtask =
        checklistDrafts[subtask.id] || hydrateExtraFields(items, subtask.checklist?.answers);
      const extraError = validateExtraFieldsFromNatives(items, {}, extrasForSubtask);
      if (extraError) {
        setError(extraError);
        return;
      }
    }
    const hasThread = showsSubtaskThread(subtask);
    const hasDraft =
      hasThread &&
      (!isBlankRichText(replyDrafts[subtask.id] || '') || Boolean((pendingFiles[subtask.id] || []).length));
    const concludeLabel = subtask.conclusion?.button_label || 'Concluir';
    const ok = await showConfirm({
      title: 'Concluir subtarefa',
      message: hasDraft
        ? 'Tem certeza que deseja concluir esta subtarefa? A mensagem e os anexos da caixa serão gravados na conversa.'
        : 'Tem certeza que deseja concluir esta subtarefa?',
      confirmLabel: concludeLabel,
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setError(null);
    try {
      if (items.length) {
        const saved = await persistChecklist(subtask, true);
        if (!saved) return;
      }
      if (hasThread) {
        await persistReply(subtask);
      }
      await changesApi.concludeSubtask(change.id, subtask.id);
      const stillImplementar = change.status === 'implementar';
      showAlert({
        title: 'Subtarefa concluída',
        message: stillImplementar
          ? 'Esta subtarefa foi concluída com sucesso. A mudança continua em Implementar até todas as subtarefas (internas e externas) estarem concluídas e os requisitos para Revisar serem atendidos.'
          : 'A subtarefa interna foi concluída.',
        variant: 'success',
      });
      await onUpdated();
    } catch (err: unknown) {
      const detail = getApiErrorMessage(err, '');
      const looksLikeStatusGate =
        /ir para Revisar|antes de avançar para este status|subtarefas internas e externas/i.test(
          detail,
        );
      setError(
        looksLikeStatusGate
          ? 'Não foi possível concluir a subtarefa por um conflito com o avanço automático de status. Atualize a página e tente de novo; se persistir, avise o suporte.'
          : getApiErrorMessage(err, 'Falha ao concluir a subtarefa.'),
      );
    }
  };

  const handleStatus = async (subtask: ChangeSubtask, status: string) => {
    if (status === 'concluida') {
      await handleConclude(subtask);
      return;
    }
    setError(null);
    try {
      await changesApi.updateSubtask(change.id, subtask.id, { status });
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao atualizar a subtarefa.'));
    }
  };

  const handleAssume = async (subtask: ChangeSubtask) => {
    const ok = await showConfirm({
      title: 'Assumir subtarefa',
      message: `Tem certeza que deseja assumir "${subtask.title}"? Depois você responde e conclui esta subtarefa.`,
      confirmLabel: 'Assumir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setError(null);
    try {
      await changesApi.assumeSubtask(change.id, subtask.id);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao assumir a subtarefa.'));
    }
  };

  const handleReply = async (subtask: ChangeSubtask) => {
    const content = replyDrafts[subtask.id] || '';
    const files = pendingFiles[subtask.id] || [];
    if (isBlankRichText(content) && !files.length) {
      setError('Escreva a resposta ou anexe um arquivo.');
      return;
    }
    const ok = await showConfirm({
      title: 'Enviar mensagem',
      message: 'Tem certeza que deseja enviar esta mensagem?',
      confirmLabel: 'Enviar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setError(null);
    try {
      await persistReply(subtask);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao gravar a resposta.'));
    }
  };

  const handleAttachOnly = async (subtask: ChangeSubtask) => {
    const files = pendingFiles[subtask.id] || [];
    if (!files.length) {
      setError('Selecione um arquivo.');
      return;
    }
    setError(null);
    try {
      for (const file of files) {
        const invalid = validateAttachmentFile(file);
        if (invalid) {
          setError(invalid);
          return;
        }
        const formData = new FormData();
        formData.append('file', file);
        await changesApi.uploadSubtaskAttachment(change.id, subtask.id, formData);
      }
      setPendingFiles((prev) => ({ ...prev, [subtask.id]: [] }));
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao enviar o anexo.'));
    }
  };

  const addPendingFiles = (subtaskId: number, fileList: FileList | null) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setPendingFiles((prev) => ({
      ...prev,
      [subtaskId]: [...(prev[subtaskId] || []), ...incoming],
    }));
    setFileInputKey((prev) => ({ ...prev, [subtaskId]: (prev[subtaskId] || 0) + 1 }));
  };

  const confirmRemoveAttachment = (filename: string) =>
    showConfirm({
      title: 'Remover anexo',
      message: `Tem certeza que deseja remover "${filename}"?`,
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });

  const removePendingFile = async (subtaskId: number, index: number, filename: string) => {
    const ok = await confirmRemoveAttachment(filename);
    if (!ok) return;
    setPendingFiles((prev) => ({
      ...prev,
      [subtaskId]: (prev[subtaskId] || []).filter((_, itemIndex) => itemIndex !== index),
    }));
    setFileInputKey((prev) => ({ ...prev, [subtaskId]: (prev[subtaskId] || 0) + 1 }));
  };

  const handleRemoveSavedAttachment = async (subtask: ChangeSubtask, attachmentId: number, filename: string) => {
    const ok = await confirmRemoveAttachment(filename);
    if (!ok) return;
    setError(null);
    try {
      await changesApi.deleteSubtaskAttachment(change.id, subtask.id, attachmentId);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao remover o anexo.'));
    }
  };

  const isSectorMember = (groupId: number) =>
    (areas.find((area) => area.group_id === groupId)?.members || []).some(
      (member) => member.user_id === user?.id,
    );

  const canAssumeSubtask = (subtask: ChangeSubtask) =>
    subtask.can_assume_subtask ??
    canAssumeByRule({
      changeOperable: canOperate,
      status: subtask.status,
      assignmentKind: subtask.assignment_kind,
      assigneeId: subtask.assignee_id,
      isSectorMember: isSectorMember(subtask.group_id),
      userId: user?.id,
    });

  const canTalk = (subtask: ChangeSubtask) =>
    Boolean(subtask.can_interact_create) &&
    canTalkByRule({
      changeFrozen: FROZEN_STATUSES.has(change.status),
      hasTicket: Boolean(subtask.ticket_id),
      assignmentKind: subtask.assignment_kind,
      assigneeId: subtask.assignee_id,
      createdBy: subtask.created_by,
      isSectorMember: isSectorMember(subtask.group_id),
      isStakeholder: false,
      userId: user?.id,
    });

  const canAttach = (subtask: ChangeSubtask) =>
    Boolean(subtask.can_interact_create) &&
    canAttachByRule({
      changeFrozen: FROZEN_STATUSES.has(change.status),
      assignmentKind: subtask.assignment_kind,
      assigneeId: subtask.assignee_id,
      createdBy: subtask.created_by,
      isSectorMember: isSectorMember(subtask.group_id),
      isStakeholder: false,
      userId: user?.id,
    });

  const canAnswer = (subtask: ChangeSubtask) =>
    canAnswerByRule({
      changeOperable: canOperate,
      status: subtask.status,
      assignmentKind: subtask.assignment_kind,
      assigneeId: subtask.assignee_id,
      responsibleId: subtask.responsible_id,
      ticketId: subtask.ticket_id,
      isSectorMember: isSectorMember(subtask.group_id),
      userId: user?.id,
    });

  const listedSubtasks: ChangeSubtask[] = useMemo(
    () => sortListedSubtasks((change.subtasks || []).filter(isListableSubtask)),
    [change.subtasks],
  );

  const internalListedSubtasks = useMemo(
    () => listedSubtasks.filter((item) => isInternalSubtask(item)),
    [listedSubtasks],
  );

  const externalListedSubtasks = useMemo(
    () => listedSubtasks.filter((item) => !isInternalSubtask(item)),
    [listedSubtasks],
  );

  useEffect(() => {
    void changesApi
      .getInternalSubtaskListLayout()
      .then((data) => setInternalListColumns(layoutColumnsFromApi(data.columns || [])))
      .catch(() => setInternalListColumns([]));
  }, []);

  const handleRedispatchPackage = async () => {
    setError(null);
    const leftover = occupyingSubtaskGroupIds(change.subtasks);
    if (leftover.size) {
      const ok = await showConfirm({
        title: 'Redisparar internas e externas',
        message:
          'As subtarefas ainda ativas desta rodada serão canceladas. Os chamados no Incidente não serão fechados. Depois você confirma o novo pacote.',
        variant: 'warning',
        confirmLabel: 'Cancelar e continuar',
        cancelLabel: 'Voltar',
      });
      if (!ok) return;
      setResettingPackage(true);
      try {
        await changesApi.resetToScheduled(change.id);
        await onUpdated();
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, 'Não foi possível cancelar as subtarefas desta rodada.'));
        setResettingPackage(false);
        return;
      }
      setResettingPackage(false);
    }
    setPackageModalPrefill(true);
    setShowPackageModal(true);
  };

  const handleDownloadAttachment = (file: ChangeSubtaskAttachment) => {
    if (!file.file_path) return;
    setError(null);
    void downloadFile(file.file_path, file.filename, {
      onError: (message) => setError(message),
      onSessionExpired: () => setError('Sessão expirada. Faça login novamente.'),
      onFileMissing: (payload) =>
        setError(payload.detail || 'O arquivo não foi encontrado no armazenamento.'),
    });
  };

  const sendGroup =
    sendIncidentGroupId == null
      ? null
      : quickIncidentGroups.find((item) => item.group_id === sendIncidentGroupId) || null;
  const incidentPeople = peopleFromAreas(areas).map((member) => ({
    id: member.user_id,
    name: member.name || `Usuário #${member.user_id}`,
    email: '',
  }));

  return (
    <div>
      {error && !sendGroup ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert variant="success" dismissible onClose={() => setInfo(null)}>
          {info}
        </Alert>
      ) : null}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h6 className="mb-2">Subtarefas</h6>
        <div className="d-flex flex-wrap gap-2">
          {canShowRedispatchPackage(change, user) ? (
            <Button
              size="sm"
              variant="outline-primary"
              disabled={resettingPackage}
              onClick={() => void handleRedispatchPackage()}
            >
              {resettingPackage ? 'Preparando…' : 'Redisparar internas e externas'}
            </Button>
          ) : null}
          {showManualCreate ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setError(null);
                setPackageModalPrefill(false);
                setShowPackageModal(true);
              }}
            >
              Adicionar subtarefa
            </Button>
          ) : null}
          {canQuickSendIncident && quickIncidentGroups.length ? (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setError(null);
                const first = quickIncidentGroups[0];
                setSendIncidentGroupId(first.group_id);
                if (change.status === 'transporte') {
                  setQuickDrafts((prev) => ({
                    ...prev,
                    [first.group_id]: emptyIncidentDraft(first, { blankWorkflow: true }),
                  }));
                }
              }}
            >
              Abrir chamado no Incidente
            </Button>
          ) : null}
        </div>
      </div>
      {openSubtaskId == null &&
      listedSubtasks.length === 0 &&
      !(canQuickSendIncident && quickIncidentGroups.length) ? (
        <Alert variant="info" className="mb-3">
          Nenhuma subtarefa ainda.
          {showManualCreate
            ? ' Sem ao menos uma subtarefa com grupo, a mudança não sai de Agendado e as aprovações não disparam. Use Adicionar subtarefa e avance o status para Autorizar.'
            : !allowInternalSubtasks && allowExternalSubtasks
              ? ' Para este tipo, use chamados no Incidente (botão acima ou na criação da mudança).'
              : ' Este tipo não permite criar subtarefas manuais aqui; confira o cadastro Tipos de mudança.'}
        </Alert>
      ) : null}
      {openSubtaskId == null && listedSubtasks.length ? (
        <div className="mb-3">
          {internalListedSubtasks.length ? (
            <section
              className="change-internal-subtask-list-section"
              aria-labelledby="change-internal-subtasks-heading"
            >
              <h6 className="mb-2" id="change-internal-subtasks-heading">
                Subtarefas internas ({internalListedSubtasks.length})
              </h6>
              <ChangeInternalSubtaskListTable
                subtasks={internalListedSubtasks}
                columns={
                  internalListColumns.length
                    ? internalListColumns
                    : INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS
                }
                catalog={INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS}
                onOpenSubtask={setOpenSubtaskId}
              />
            </section>
          ) : null}
          {externalListedSubtasks.length ? (
            <section
              className={
                internalListedSubtasks.length ? 'change-internal-subtask-list-section' : undefined
              }
              aria-labelledby="change-external-subtasks-heading"
            >
              <h6 className="mb-2" id="change-external-subtasks-heading">
                Subtarefas externas ({externalListedSubtasks.length})
              </h6>
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Subtarefa</th>
                    <th>Grupo associado</th>
                    <th>Status</th>
                    <th>Membro do grupo</th>
                    <th>Tipo da tarefa</th>
                    <th className="text-end">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {externalListedSubtasks.map((subtask) => (
                    <tr key={subtask.id}>
                      <td>{subtask.title}</td>
                      <td>{subtask.group_name || '—'}</td>
                      <td>{subtaskStatusBadge(subtask)}</td>
                      <td>{subtaskMemberLabel(subtask)}</td>
                      <td>{subtaskTypeLabel(subtask)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setOpenSubtaskId(subtask.id)}
                          aria-label={`Ver ${subtask.title}`}
                        >
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          ) : null}
        </div>
      ) : null}
      {openSubtaskId != null ? (
        <div className="mb-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setOpenSubtaskId(null)}>
            Voltar
          </Button>
        </div>
      ) : null}
      {listedSubtasks.filter((item) => item.id === openSubtaskId).map((subtask) => {
        const thread = replies[subtask.id] || [];
        const { byReplyId, leftover } = splitSubtaskAttachments(subtask.attachments || [], thread);
        const questions = checklistItems(subtask);
        const headerHint =
          incidentHeaderHint(subtask) ||
          checklistHeaderHint(subtask, canAnswer(subtask), canAssumeSubtask(subtask));
        const isIncident =
          subtask.origin === 'incidente' ||
          Boolean(subtask.gates_implementar) ||
          Boolean(subtask.incident_dispatch_pending);
        return (
          <Card key={subtask.id} className="mb-3">
          <Card.Header className="d-flex justify-content-between flex-wrap gap-2 align-items-center">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <strong>{subtask.title}</strong>
              {subtask.origin === 'checklist' ? <Badge bg="info">Checklist</Badge> : null}
              {isIncident ? <Badge bg="secondary">Incidente</Badge> : null}
              {subtask.gates_implementar ? (
                <Badge bg="warning" text="dark">
                  Gate Implementar
                </Badge>
              ) : null}
              {headerHint ? (
                <Badge bg="light" text="dark">
                  {headerHint}
                </Badge>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2">{subtaskStatusBadge(subtask)}</div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Field label="Grupo">{subtask.group_name || '—'}</Field>
              <Field label="Tipo da tarefa">{subtaskTypeLabel(subtask)}</Field>
              <Field label="Destino">
                {assignmentLabel(subtask.assignment_kind, subtask.assignee_name)}
              </Field>
              {subtask.due_date ? (
              <Field label="Data">{formatGovernanceDate(subtask.due_date)}</Field>
              ) : null}
              <Field label="Responsável">{subtaskResponsibleLabel(subtask)}</Field>
              <Col md={12} className="mb-2">
                <div className="small text-muted">Pedido</div>
                <div>{subtask.comments?.trim() ? subtask.comments : '—'}</div>
              </Col>
              {extraEntries(subtask.extra_fields, schema).map((item) => (
                <Field key={item.id} label={item.label}>
                  {item.value}
                </Field>
              ))}
            </Row>
            {subtask.conclusion?.waiting_message ? (
              <Alert variant="info" className="mb-3">
                {subtask.conclusion.waiting_message}
              </Alert>
            ) : null}
            {isIncident ? (
              <Alert variant="light" className="mb-3">
                {subtask.ticket_id ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Link to={`/tickets/${subtask.ticket_id}`}>Chamado #{subtask.ticket_id}</Link>
                    {subtask.incident?.status ? (
                      <span className="small text-muted">Status: {subtask.incident.status}</span>
                    ) : null}
                    {subtask.incident?.taken_by_name ? (
                      <span className="small text-muted">
                        Assumido por {subtask.incident.taken_by_name}
                      </span>
                    ) : (
                      <span className="small text-muted">
                        Aguardando alguém assumir no módulo de Incidentes.
                      </span>
                    )}
                  </div>
                ) : subtask.incident_dispatch_pending || subtask.gates_implementar ? (
                  <span className="small text-muted">
                    Disparo agendado: o chamado será aberto no Incidente automaticamente após GTEC e
                    GSERV-I aprovarem o Transporte.
                  </span>
                ) : (
                  <span className="small text-muted">Chamado ainda não foi aberto.</span>
                )}
              </Alert>
            ) : subtask.invalidated_at ? (
              <Alert variant="secondary" className="py-2 small mb-3">
                Subtarefa cancelada
                {subtask.invalidation_reason ? `: ${subtask.invalidation_reason}` : '.'} O chamado no
                Incidente, se houver, permanece aberto.
              </Alert>
            ) : allowInternalSubtasks ? (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {canAssumeSubtask(subtask) ? (
                <Button size="sm" onClick={() => void handleAssume(subtask)}>
                  Assumir
                </Button>
              ) : null}
              {(subtask.conclusion
                ? subtask.conclusion.can_conclude
                : canTalk(subtask) && subtask.status === 'em_atendimento') ? (
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={() => void handleStatus(subtask, 'concluida')}
                >
                  {subtask.conclusion?.button_label || 'Concluir'}
                </Button>
              ) : null}
            </div>
            ) : (
              <Alert variant="light" className="mb-3 small">
                Subtarefas internas não se aplicam a este tipo de mudança.
              </Alert>
            )}
            {questions.length ? (() => {
              const { requester, assignee } = splitChecklistByRespondent(questions);
              const extras =
                checklistDrafts[subtask.id] ||
                hydrateExtraFields(questions, subtask.checklist?.answers);
              const canEditRequester = canEditRequesterChecklistFields(change, subtask, user);
              const canEditAssignee = allowInternalSubtasks && canAnswer(subtask);
              const unified = shouldUseUnifiedChecklistEditor(
                change,
                subtask,
                user,
                canEditAssignee,
                requester,
                assignee,
              );
              const allEditableFields = [...requester, ...assignee];
              return (
                <>
                  {unified ? (
                    <ChangeSubtaskChecklist
                      fields={allEditableFields}
                      extras={extras}
                      onChange={(fieldId, value) => handleChecklistChange(subtask.id, fieldId, value)}
                      onSave={() => void handleSaveChecklist(subtask)}
                      saving={savingChecklistId === subtask.id}
                      readOnly={false}
                      groupName={subtask.group_name}
                      tabs={subtask.checklist?.tabs}
                      tabsPosition={subtask.checklist?.tabs_position}
                    />
                  ) : (
                    <>
                      {requester.length ? (
                        <ChangeSubtaskChecklist
                          fields={requester}
                          extras={extras}
                          onChange={(fieldId, value) => handleChecklistChange(subtask.id, fieldId, value)}
                          onSave={() => void handleSaveChecklist(subtask)}
                          saving={savingChecklistId === subtask.id}
                          readOnly={!canEditRequester}
                          groupName={subtask.group_name}
                          tabs={subtask.checklist?.tabs}
                          tabsPosition={subtask.checklist?.tabs_position}
                          waitingMessage="Preenchimento do solicitante ou dono da mudança."
                        />
                      ) : null}
                      {assignee.length ? (
                        <ChangeSubtaskChecklist
                          fields={assignee}
                          extras={extras}
                          onChange={(fieldId, value) => handleChecklistChange(subtask.id, fieldId, value)}
                          onSave={() => void handleSaveChecklist(subtask)}
                          saving={savingChecklistId === subtask.id}
                          readOnly={!canEditAssignee}
                          groupName={subtask.group_name}
                          tabs={subtask.checklist?.tabs}
                          tabsPosition={subtask.checklist?.tabs_position}
                          waitingMessage={
                            canAssumeSubtask(subtask)
                              ? 'Assuma a subtarefa para responder as perguntas do grupo.'
                              : undefined
                          }
                        />
                      ) : null}
                    </>
                  )}
                </>
              );
            })() : null}
            {subtask.origin === 'checklist' && !showsSubtaskThread(subtask) ? (
              <p className="small text-muted mb-3">
                {canAnswer(subtask)
                  ? 'Responda as perguntas e salve. Comentários e anexos não entram neste checklist.'
                  : canAssumeSubtask(subtask)
                    ? 'Assuma a subtarefa para responder as perguntas. Comentários e anexos não entram neste checklist.'
                    : 'Quem for do grupo assume e responde as perguntas. Comentários e anexos não entram neste checklist.'}
              </p>
            ) : null}
            {showsSubtaskThread(subtask) ? (
            <div>
              <div className="small text-muted mb-2">Conversa</div>
              {subtask.status === 'concluida' ? (
                <p className="small text-muted mb-2">
                  A subtarefa está concluída. A conversa e os anexos continuam visíveis;
                  quem pode vê-la ainda responde aqui.
                </p>
              ) : null}
              {thread.length === 0 && leftover.length === 0 ? (
                <p className="small text-muted">
                  Nenhuma mensagem ainda. Concluir sem enviar texto ou anexo deixa este
                  histórico vazio.
                </p>
              ) : null}
              {thread.map((reply) => (
                <div key={reply.id} className="border rounded bg-light px-3 py-2 mb-2">
                  <div className="d-flex justify-content-between gap-2 small text-muted mb-1">
                    <strong>{reply.user_name || 'Participante'}</strong>
                    <span>{formatReplyWhen(reply.created_at)}</span>
                  </div>
                  {reply.content && reply.content !== FILE_ONLY_REPLY ? (
                    <div
                      className="comment-content csp-wave1-comment-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(reply.content) }}
                    />
                  ) : null}
                  {(byReplyId[reply.id] || []).length ? (
                    <ul className="small mb-0 mt-2">
                      {(byReplyId[reply.id] || []).map((file) => (
                        <AttachmentLine
                          key={file.id}
                          file={file}
                          canRemove={canAttach(subtask)}
                          onRemove={() =>
                            void handleRemoveSavedAttachment(subtask, file.id, file.filename)
                          }
                          onDownload={() => handleDownloadAttachment(file)}
                        />
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
              {leftover.length ? (
                <div className="border rounded px-3 py-2 mb-2">
                  <div className="small text-muted mb-1">Anexos sem mensagem</div>
                  <ul className="small mb-0">
                    {leftover.map((file) => (
                      <AttachmentLine
                        key={file.id}
                        file={file}
                        canRemove={canAttach(subtask)}
                        onRemove={() =>
                          void handleRemoveSavedAttachment(subtask, file.id, file.filename)
                        }
                        onDownload={() => handleDownloadAttachment(file)}
                      />
                    ))}
                  </ul>
                </div>
              ) : null}
              {canTalk(subtask) && subtask.ticket_id ? (
                <div className="mt-2">
                  <RichTextCommentEditor
                    compact
                    ariaLabel={SUBTASK_REPLY_LABEL}
                    value={replyDrafts[subtask.id] || ''}
                    onChange={(html) =>
                      setReplyDrafts((prev) => ({ ...prev, [subtask.id]: html }))
                    }
                  />
                  <Form.Group className="mt-2">
                    <Form.Label className="small">Anexar na resposta</Form.Label>
                    <Form.Control
                      key={`${subtask.id}-${fileInputKey[subtask.id] || 0}`}
                      type="file"
                      multiple
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        addPendingFiles(subtask.id, input.files);
                      }}
                    />
                    {(pendingFiles[subtask.id] || []).map((file, index) => (
                      <div key={`${file.name}-${index}`} className="d-flex align-items-center gap-2 small mt-1">
                        <span>{file.name}</span>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => void removePendingFile(subtask.id, index, file.name)}
                        >
                          Remover
                        </Button>
                      </div>
                    ))}
                  </Form.Group>
                  <Button size="sm" className="mt-2" onClick={() => void handleReply(subtask)}>
                    Enviar mensagem
                  </Button>
                </div>
              ) : canAttach(subtask) ? (
                <div className="mt-2">
                  <Form.Group>
                    <Form.Label className="small">Anexar arquivo</Form.Label>
                    <Form.Control
                      key={`${subtask.id}-${fileInputKey[subtask.id] || 0}`}
                      type="file"
                      accept={ATTACHMENT_ACCEPT}
                      multiple
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        addPendingFiles(subtask.id, input.files);
                      }}
                    />
                    {(pendingFiles[subtask.id] || []).map((file, index) => (
                      <div key={`${file.name}-${index}`} className="d-flex align-items-center gap-2 small mt-1">
                        <span>{file.name}</span>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => void removePendingFile(subtask.id, index, file.name)}
                        >
                          Remover
                        </Button>
                      </div>
                    ))}
                  </Form.Group>
                  <Button size="sm" className="mt-2" onClick={() => void handleAttachOnly(subtask)}>
                    Enviar anexo
                  </Button>
                </div>
              ) : null}
            </div>
            ) : null}
          </Card.Body>
        </Card>
        );
      })}

      <ChangeAddSubtasksPackageModal
        show={showPackageModal}
        change={change}
        users={peopleFromAreas(areas).map(
          (member) =>
            ({
              id: member.user_id,
              name: member.name || `Usuário #${member.user_id}`,
              email: '',
            }) as User,
        )}
        allowInternalSubtasks
        allowExternalSubtasks
        unrestrictedManualCreate
        prefillLikeCreate={packageModalPrefill}
        onHide={() => {
          setShowPackageModal(false);
          setPackageModalPrefill(false);
        }}
        onSaved={onUpdated}
      />
      <Modal
        show={showCreateModal}
        onHide={() => undefined}
        backdrop="static"
        keyboard={false}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Nova subtarefa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {error ? (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}
            {useConfigurableForm ? (
              <ChangeSubtaskConfigurableForm
                fields={schema}
                form={createForm}
                extras={extras}
                onFormChange={(patch) => {
                  if (patch.title !== undefined) setTitle(patch.title);
                  if (patch.assignmentKind !== undefined) setAssignmentKind(patch.assignmentKind);
                  if (patch.groupId !== undefined) applyGroupSelection(patch.groupId);
                  if (patch.assigneeId !== undefined) setAssigneeId(patch.assigneeId);
                  if (patch.dueDate !== undefined) setDueDate(patch.dueDate);
                  if (patch.comments !== undefined) setComments(patch.comments);
                  if (patch.taskTypeId !== undefined) setTaskTypeId(patch.taskTypeId);
                }}
                taskTypes={taskTypes}
                onExtraChange={(fieldId, value) =>
                  setExtras((prev) => ({ ...prev, [String(fieldId)]: value }))
                }
                areas={areasForManualCreate}
                windowStart={windowStart}
                windowEnd={windowEnd}
                windowHelp={windowHelp}
              />
            ) : (
            <Row className="g-2">
              {taskTypes.length ? (
                <Col md={12}>
                  <Form.Label htmlFor="subtask-task-type">Tipo da tarefa</Form.Label>
                  <Form.Select
                    id="subtask-task-type"
                    aria-label="Tipo da tarefa"
                    value={taskTypeId}
                    onChange={(e) => setTaskTypeId(e.target.value)}
                  >
                    <option value="">Selecione o tipo da tarefa</option>
                    {taskTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              ) : null}
              <Col md={5}>
                <Form.Label>Título</Form.Label>
                <Form.Control
                  maxLength={CHANGE_FIELD_LIMITS.title}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Col>
              <Col md={7}>
                <Form.Label>Enviar para</Form.Label>
                <div className="d-flex flex-wrap gap-3 mb-1">
                  <Form.Check
                    type="radio"
                    id="subtask-dest-setor"
                    name="subtask-assignment"
                    label="Grupo"
                    checked={assignmentKind === ASSIGNMENT_SETOR}
                    onChange={() => {
                      setAssignmentKind(ASSIGNMENT_SETOR);
                      setAssigneeId('');
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="subtask-dest-pessoa"
                    name="subtask-assignment"
                    label="Pessoa específica"
                    checked={assignmentKind === ASSIGNMENT_PESSOA}
                    onChange={() => {
                      setAssignmentKind(ASSIGNMENT_PESSOA);
                      setGroupId('');
                    }}
                  />
                </div>
              </Col>
              <Col md={4}>
                <Form.Label>Grupo</Form.Label>
                <Form.Select
                  value={groupId}
                  aria-label="Grupo que vai receber a subtarefa"
                  disabled={assignmentKind === ASSIGNMENT_PESSOA}
                  onChange={(e) => {
                    applyGroupSelection(e.target.value);
                    setAssigneeId('');
                  }}
                >
                  <option value="">
                    {assignmentKind === ASSIGNMENT_PESSOA
                      ? 'Não se aplica'
                      : 'Selecione o grupo que vai receber'}
                  </option>
                  {areasForManualCreate.map((area) => (
                    <option key={area.group_id} value={area.group_id}>
                      {area.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {assignmentKind === ASSIGNMENT_PESSOA
                    ? 'O grupo é definido automaticamente pela pessoa escolhida.'
                    : 'Membros deste grupo assumem e respondem.'}
                </Form.Text>
              </Col>
              {assignmentKind === ASSIGNMENT_PESSOA ? (
                <Col md={4}>
                  <Form.Label>Pessoa</Form.Label>
                  <Form.Select
                    value={assigneeId}
                    aria-label="Pessoa que vai receber a subtarefa"
                    onChange={(e) => setAssigneeId(e.target.value)}
                  >
                    <option value="">Selecione a pessoa</option>
                    {peopleFromAreas(areas).map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.name || `Usuário #${member.user_id}`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Somente esta pessoa poderá assumir.</Form.Text>
                </Col>
              ) : null}
              {schema.find((field) => field.native_key === 'due_date')?.is_visible ? (
              <Col md={3}>
                <Form.Label>Data</Form.Label>
                <Form.Control
                  type="date"
                  min={windowStart}
                  max={windowEnd}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <Form.Text className="text-muted">{windowHelp}</Form.Text>
              </Col>
              ) : null}
              <Col md={12}>
                <Form.Label>{opensIncident ? 'Descrição do chamado' : 'Comentário inicial'}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  aria-label={opensIncident ? 'Descrição do chamado' : 'Comentário inicial'}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </Col>
            </Row>
            )}
            {canOfferImplementationGate ? (
              <Form.Group className="mt-2">
                <Form.Check
                  type="switch"
                  id="create-subtask-gate-implementar"
                  label="Gate de Implementar (Infra)"
                  checked={createGatesImplementar}
                  onChange={(event) => setCreateGatesImplementar(event.target.checked)}
                />
                <div className="form-text">{IMPLEMENTATION_GATE_HELP}</div>
              </Form.Group>
            ) : null}
            {opensIncident ? (
              <Alert variant="info" className="mt-2 mb-0">
                {createGatesImplementar && canOfferImplementationGate
                  ? 'Os dados abaixo serão usados quando o chamado for disparado automaticamente em Transporte.'
                  : 'Este grupo abre um chamado no módulo de Incidentes. Escolha o fluxo para abrir as mesmas abas do Novo Ticket. Quem assume e encerra é lá.'}
              </Alert>
            ) : null}
            {opensIncident ? (
              <IncidentTicketFieldsForm
                workflowId={workflowId ? Number(workflowId) : ''}
                incidentsGroupId={selectedArea?.incidents_group_id}
                values={createCustomValues}
                files={createCustomFiles}
                ariaPrefix="Solicitação da subtarefa"
                users={peopleFromAreas(areas).map((member) => ({
                  id: member.user_id,
                  name: member.name || `Usuário #${member.user_id}`,
                  email: '',
                }))}
                onValuesChange={setCreateCustomValues}
                onFilesChange={setCreateCustomFiles}
                onFieldsChange={setCreateCustomFields}
                initialValues={createCustomValues}
                infoTab={
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Label>
                        Fluxo de Trabalho <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        aria-label="Fluxo do chamado"
                        value={workflowId}
                        onChange={(e) => applyIncidentWorkflow(e.target.value)}
                      >
                        <option value="">Selecione um fluxo</option>
                        {incidentWorkflows.map((flow) => (
                          <option key={flow.id} value={flow.id}>
                            {flow.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label>Urgência</Form.Label>
                      <Form.Select
                        aria-label="Urgência do chamado"
                        value={urgency}
                        disabled={!workflowId}
                        onChange={(e) => setUrgency(e.target.value)}
                      >
                        <option value="">Selecione</option>
                        <option value="ALTA">Alta</option>
                        <option value="MEDIA">Média</option>
                        <option value="BAIXA">Baixa</option>
                      </Form.Select>
                    </Col>
                  </Row>
                }
              />
            ) : null}
            {showCreateAttachments ? (
              <Form.Group className="mt-2">
                <Form.Label className="small">Anexos na criação</Form.Label>
                <Form.Control
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  aria-label="Anexos na criação"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const incoming = Array.from(input.files || []);
                    const invalid = incoming.map(validateAttachmentFile).find(Boolean);
                    if (invalid) {
                      setError(invalid);
                      return;
                    }
                    setCreateFiles(incoming);
                  }}
                />
                {createFiles.map((file) => (
                  <div key={file.name} className="small mt-1">{file.name}</div>
                ))}
              </Form.Group>
            ) : null}
            <Button className="mt-2" onClick={() => void handleCreate()}>
              Criar subtarefa
            </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setError(null);
              setShowCreateModal(false);
            }}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
      {!canCreate && !listedSubtasks.length && !(canQuickSendIncident && quickIncidentGroups.length) ? (
        <Alert variant="light">
          {FORM_EDITABLE_STATUSES.has(change.status)
            ? 'As subtarefas automáticas do checklist abrem ao criar a Mudança Normal, só para grupos com perguntas. Subtarefas manuais podem ser criadas em Agendado.'
            : 'Subtarefas desta mudança.'}
        </Alert>
      ) : null}
      <Modal
        show={Boolean(sendGroup)}
        onHide={() => undefined}
        backdrop="static"
        keyboard={false}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Chamado no Incidente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Escolha o fluxo, revise os campos e envie. O chamado abre no módulo de Incidentes e
            esta subtarefa passa a acompanhar o ticket.
          </p>
          {quickIncidentGroups.length > 1 ? (
            <Form.Group className="mb-3">
              <Form.Label>Grupo</Form.Label>
              <Form.Select
                aria-label="Grupo do chamado no Incidente"
                value={sendIncidentGroupId ?? ''}
                onChange={(event) => {
                  setError(null);
                  const nextId = Number(event.target.value);
                  setSendIncidentGroupId(nextId);
                  const group = quickIncidentGroups.find((item) => item.group_id === nextId);
                  if (group && change.status === 'transporte') {
                    setQuickDrafts((prev) => ({
                      ...prev,
                      [nextId]: emptyIncidentDraft(group, { blankWorkflow: true }),
                    }));
                  }
                }}
              >
                {quickIncidentGroups.map((group) => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.name || `Grupo #${group.group_id}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}
          {error ? (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          {sendGroup ? (
            <IncidentSendPackageForm
              group={sendGroup}
              draft={
                quickDrafts[sendGroup.group_id] ||
                emptyIncidentDraft(
                  sendGroup,
                  change.status === 'transporte' ? { blankWorkflow: true } : undefined,
                )
              }
              workflows={incidentTargets.workflows}
              users={incidentPeople}
              onPatch={(patch) => patchQuickDraft(sendGroup, patch)}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={quickSending !== null}
            onClick={() => {
              setError(null);
              setSendIncidentGroupId(null);
            }}
          >
            Fechar
          </Button>
          <Button
            disabled={!sendGroup || quickSending !== null}
            onClick={() => {
              if (sendGroup) void handleQuickSend(sendGroup);
            }}
          >
            {quickSending !== null ? 'Enviando...' : 'Enviar chamado'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ChangeSubtasksTab;
